import { Injectable } from '@angular/core';
import { PlanFeatureDto } from '../../../services/plan/plan.interface';
import {
  APIRowData,
  License,
  Module,
  OrganizationLicenseDialogData,
  OrganizationModulesDialogData
} from '../interfaces/organization-license.interface';

/**
 * Service responsible for mapping grid data to dialog data structures.
 */
@Injectable({ providedIn: 'root' })
export class DialogDataMapperService {
  /**
   * Maps selected grid rows to dialog data for the license dialog.
   * @param selections - The selected grid rows.
   * @param mode - Whether creating or editing.
   * @returns The dialog data for the license dialog.
   */
  public mapToLicenseDialogData(
    selections: Array<APIRowData>,
    mode: 'create' | 'edit'
  ): OrganizationLicenseDialogData {
    if (mode === 'create' || selections.length === 0) {
      return this.createEmptyLicenseData();
    }

    const firstRow: APIRowData = selections[0];
    return {
      mode: 'edit',
      tenantName: firstRow.tenantName,
      tenantId: firstRow.tenantId,
      environmentId: firstRow.environmentId,
      licenses: selections.map((row: APIRowData): License => this.mapRowToLicense(row))
    };
  }

  /**
   * Maps plan feature DTOs to a modules dialog data structure.
   * Now uses PlanFeatureDto with nested plan/feature objects.
   * @param planFeatures - The plan features from the API.
   * @param planId - The plan ID.
   * @param planName - The plan name.
   * @param planType - The plan type.
   * @returns The dialog data for the modules dialog.
   */
  public mapToModulesDialogData(
    planFeatures: Array<PlanFeatureDto>,
    planId: string,
    planName: string,
    planType: string
  ): OrganizationModulesDialogData {
    return {
      id: planId,
      planName,
      planType,
      modules: planFeatures.map((pf: PlanFeatureDto): Module => ({
        moduleName: pf.feature.name,
        moduleVersion: '1.0.0',
        trial: false,
        description: pf.feature.description ?? ''
      }))
    };
  }

  /**
   * Maps a grid row to a License object.
   * @param row - The grid row data.
   * @returns A License object populated from the row.
   */
  private mapRowToLicense(row: APIRowData): License {
    return {
      id: row.id,
      planName: row.planName || '',
      planId: row.planId || '',
      planType: row.planType || '',
      environmentId: row.environmentId || '',
      environmentName: row.environmentName || '',
      maxUsers: row.maxUsers || 1,
      isActive: row.isActive ?? true,
      activatedOnUtc: row.activatedOnUtc,
      expiresOnUtc: row.expiresOnUtc
    };
  }

  /**
   * Creates an empty license dialog data object for new entries.
   * @returns Empty dialog data for create mode.
   */
  private createEmptyLicenseData(): OrganizationLicenseDialogData {
    return {
      mode: 'create',
      tenantName: '',
      tenantId: '',
      licenses: []
    };
  }
}
